
import { Type } from '@deqo/foundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { Component, ObjectManager } from '../../../concepts/objects';
import { resolveSubject, resolveSubjects } from '../../canvas/commands/CommandUtil';
import { clearSelection } from '../../canvas/commands/SelectionCommands';
import { Phys } from '../../canvas/entities/Phys';
import { Physicals } from '../../canvas/entities/Physicals';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { alert } from '../alerts/AlertCommands';
import { FlexSorter } from '../autoLayout/FlexLayout';
import { Token } from '../tokens/Token';
import { Tray } from './Tray';
import { TrayModeContent } from './TrayMode';

export const createTray = defineCommand(
    'createTray',
    'Create: Tray...',
    (cc) => {
        Physicals.createAtCameraPosition(cc, Tray, x => {
            x.width = 140;
            x.height = 140;
            x.editing = true;
        });
    },
);

export const trayReset = defineCommand(
    'trayReset',
    'Tray: Reset...',
    (cc, [targetId]: string[]) => {

        const objects = cc.resolve(ObjectManager);
        const ptpts = cc.resolve(ParticipantManager);
        const tray = resolveSubject(cc, targetId)?.as(Tray);

        if (!tray?.mode) return;
        if (tray.mode === 'none') return;
        if (tray.mode === 'custom') return;

        const items = [] as ({ type: Type<Component> } & Record<string, any>)[];

        if (tray.mode in TrayModeContent) {
            items.push(...TrayModeContent[tray.mode]);
        }
        else if (tray.mode === 'participants') {
            items.push(...ptpts.filter(x => x.joined).map(x => ({
                type: Token,
                value: `(${x.id})`,
                background: x.color,
            })));
        }

        if (!items.length) return;

        objects.update(u => {

            u.deleteAll(
                Array.from(tray.children).map(x => x.removeSelf()),
                'cascade'
            );

            for (const { type, ...props } of items) {
                const item = u.create(type);
                for (const [key, value] of Object.entries(props)) {
                    item.set(key, value);
                }
                tray.add(item);
            }
        });
    }
);

export const trayInsert = defineCommand(
    'trayInsert',
    'Tray: Insert',
    (cc, [targetId, index, elmtIds]: [string, number, string | string[]]) => {

        const spatial = cc.call(SpatialIndex);
        const objects = cc.call(ObjectManager);

        const tray = objects.getMaybe(targetId, Tray);
        const elmts = Phys.from(FlexSorter.sort('row', resolveSubjects(cc, elmtIds)));
        if (!tray || !elmts.size) return;

        const existing = tray.children[0];
        if (!!existing && !elmts.allSameType(existing.type)) {
            cc.dispatch(alert, ['Sorry, the Tray element can only contain items of the same type.']);
            return false;
        }

        objects.update(() => {

            const before = tray.children[index];

            // Convert elmts to items
            for (const tm of elmts) {
                tray.addBefore(before, tm.removeSelf());
                tm.moveTo(0, 0);
                if (tm.has('userId')) tm.set('userId', null);
                if (tm.has('created')) tm.set('created', null);
                spatial.invalidate(tm.id);
            }

        });

        cc.dispatch(clearSelection);
    }
);

export const trayToggleEditing = defineCommand(
    'trayToggleEditing',
    'Tray: Toggle Editing',
    (cc, [targetIds, editing]: [string[]?, boolean?]) => {

        const objects = cc.resolve(ObjectManager);

        const subjects = Phys.from(resolveSubjects(cc, targetIds)).polarize(Tray);
        if (!subjects.size) return;

        editing = editing ?? !subjects.first().editing;

        objects.update(() => {
            for (const tray of subjects) {
                tray.editing = editing;
            }
        });
    }
);

export const inTrayCommands = () => inCommands(
    createTray,
    trayInsert,
    trayReset,
);

